import { clsx } from "clsx";
import { type FC, type ReactElement, useCallback, useEffect, useState } from "react";
import { type BaseFieldProps, Field, type WrappedFieldProps } from "redux-form";

import { usePrevious } from "widgets";

import style from "./styles/FormWidget.module.scss";

import { fieldIsInvalid, simpleFieldValidation } from "../../../app-utils/form.js";
import { type Contig, type Region } from "../../../graphql.js";
import { ContigSelector } from "../../genome-viewer/ContigSelector.js";
import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

export interface IRegionSelectorFieldsProps extends Partial<WrappedFieldProps> {
  className?: string;
  title?: string | ReactElement;
  tooltip?: string;
  horizontalLayout?: boolean;
  isDisabled?: boolean;

  genomeId?: number;
  enableInputsWithoutGenome?: boolean;
}

const RegionSelectorField: FC<IRegionSelectorFieldsProps> = (props) => {
  const selectClasses = clsx(`react-select full-width select2-offscreen`, {
    [props.className]: !!props.className,
    error: fieldIsInvalid(props),
  });

  const region: Region = props.input.value;
  // initialization of the input values should not happen asynchronously, this is why we mount this component only when the form isInitialized flag is true
  const [contig, setContig] = useState<Contig>(region?.contig);
  const [start, setStart] = useState<number>(region?.range?.start);
  const [stop, setStop] = useState<number>(region?.range?.stop);

  useEffect(() => {
    const region: Partial<Region> = contig && {
      contig: contig,
      range: {
        start: start,
        stop: stop,
      },
    };
    props.input.onChange(region);
  }, [contig, start, stop]);

  const onContigChange = useCallback((contig: Contig) => {
    setContig(contig);
    setStart(contig ? 1 : null);
    setStop(contig?.length);
  }, []);

  //if the genome is modified then we reset the contig
  const previousGenomeId = usePrevious(props.genomeId);

  useEffect(() => {
    if (previousGenomeId != null && previousGenomeId !== props.genomeId) {
      // trigger the reset of the contig if the genome does not match (or if the genome is cleared)
      onContigChange(null);
    }
  }, [previousGenomeId, props.genomeId]);

  return (
    <FormInputGroup horizontalLayout={props.horizontalLayout}>
      {props.title && <FormLabel>{props.title}</FormLabel>}
      <FormInputWidget tooltip={props.tooltip}>
        <div style={{ display: "flex", rowGap: "10px", flexDirection: "column" }}>
          <div>
            <ContigSelector
              classNames={selectClasses}
              selectedContig={contig}
              onContigChange={onContigChange}
              genomeId={props.genomeId}
            />
          </div>
          {contig && (
            <div style={{ display: "flex", columnGap: "10px" }}>
              <input
                className={clsx(props.className, style.input_widget, { [style.input_error]: fieldIsInvalid(props) })}
                type={"number"}
                placeholder={"Start"}
                value={start}
                onChange={(event) => setStart(coordinateToNumber(event.target.value))}
                disabled={!props.genomeId && !props.enableInputsWithoutGenome}
              />
              <input
                className={clsx(props.className, style.input_widget, { [style.input_error]: fieldIsInvalid(props) })}
                type={"number"}
                placeholder={"Stop"}
                value={stop}
                onChange={(event) => setStop(coordinateToNumber(event.target.value))}
                disabled={!props.genomeId && !props.enableInputsWithoutGenome}
              />
            </div>
          )}
        </div>
        {fieldIsInvalid(props) && <FormError>{props.meta.error}</FormError>}
      </FormInputWidget>
    </FormInputGroup>
  );
};

function coordinateToNumber(value: string) {
  return value === "" ? null : Number(value);
}

export const validateRegionInputField = (value: Region) => {
  const region: Region = value;
  const contig: Contig = region?.contig;
  const start: number = region?.range?.start;
  const stop: number = region?.range?.stop;

  if (!contig) {
    return;
  }

  const rangeSimpleValidation = simpleFieldValidation(start) || simpleFieldValidation(stop);
  if (rangeSimpleValidation) {
    return rangeSimpleValidation;
  }

  if (start === null || stop === null) {
    return `Both start and stop position must be specified`;
  }

  if (start < 1) {
    return `The range start must not be smaller than 1`;
  }

  if (contig && stop > contig.length) {
    return `The value is bigger than the length of the contig (${contig.length} bases)`;
  }

  if (start != undefined && stop != undefined && start > stop) {
    return "Start position must be smaller than stop position";
  }

  if (!(Number.isInteger(start) && Number.isInteger(stop))) {
    return "Decimals are not allowed as start or stop coordinates";
  }
};

export function RegionSelectorReduxFormField(
  props: IRegionSelectorFieldsProps & BaseFieldProps<IRegionSelectorFieldsProps>,
) {
  return <Field {...props} component={RegionSelectorField} />;
}
